// MCHD Room Scheduler - Holiday Management API Endpoints
// API calls for holiday management operations

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// TYPES
// =============================================================================

export interface Holiday {
  id: string;
  buildingId: string;
  dateDay: string;
  label: string;
  createdAt: string;
  building: {
    id: string;
    name: string;
    description: string | null;
  };
}

export interface Building {
  id: string;
  name: string;
  description: string | null;
}

export interface HolidayCalendarData {
  year: number;
  month: number;
  holidays: Holiday[];
  totalDays: number;
  firstDayOfMonth: number;
}

export interface GetHolidaysParams {
  page?: number;
  limit?: number;
  buildingId?: string;
  year?: number;
  month?: number;
}

export interface GetHolidaysResponse {
  success: boolean;
  data?: {
    holidays: Holiday[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetHolidaysByBuildingResponse {
  success: boolean;
  data?: {
    holidays: Holiday[];
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetHolidayCalendarResponse {
  success: boolean;
  data?: HolidayCalendarData;
  error?: {
    code: string;
    message: string;
  };
}

export interface GetHolidayByIdResponse {
  success: boolean;
  data?: {
    holiday: Holiday;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface CreateHolidayData {
  buildingId: string;
  dateDay: string;
  label: string;
}

export interface CreateHolidayResponse {
  success: boolean;
  data?: {
    holiday: Holiday;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface UpdateHolidayData {
  dateDay?: string;
  label?: string;
}

export interface UpdateHolidayResponse {
  success: boolean;
  data?: {
    holiday: Holiday;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface DeleteHolidayResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface GetBuildingsResponse {
  success: boolean;
  data?: Building[];
  error?: {
    code: string;
    message: string;
  };
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Create headers with authentication token
 * @param token - Authentication token
 * @returns Headers object
 */
function createHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

/**
 * Get all holidays with pagination and filtering
 * @param params - Query parameters
 * @param token - Authentication token
 * @returns Promise with holidays data
 */
export async function getHolidays(
  params: GetHolidaysParams = {},
  token: string
): Promise<GetHolidaysResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.buildingId) queryParams.append('buildingId', params.buildingId);
    if (params.year) queryParams.append('year', params.year.toString());
    if (params.month) queryParams.append('month', params.month.toString());

    const url = `${API_BASE_URL}/holidays/list?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(token),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching holidays:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to fetch holidays',
      },
    };
  }
}

/**
 * Get holidays by building
 * @param buildingId - Building ID
 * @param year - Optional year filter
 * @param month - Optional month filter
 * @param token - Authentication token
 * @returns Promise with holidays data
 */
export async function getHolidaysByBuilding(
  buildingId: string,
  token: string,
  year?: number,
  month?: number
): Promise<GetHolidaysByBuildingResponse> {
  try {
    const queryParams = new URLSearchParams();
    
    if (year) queryParams.append('year', year.toString());
    if (month) queryParams.append('month', month.toString());

    const url = `${API_BASE_URL}/holidays/building/${buildingId}?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(token),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching holidays by building:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to fetch holidays for building',
      },
    };
  }
}

/**
 * Get holiday calendar for a building
 * @param buildingId - Building ID
 * @param year - Year
 * @param month - Month (1-12)
 * @param token - Authentication token
 * @returns Promise with calendar data
 */
export async function getHolidayCalendar(
  buildingId: string,
  year: number,
  month: number,
  token: string
): Promise<GetHolidayCalendarResponse> {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('year', year.toString());
    queryParams.append('month', month.toString());

    const url = `${API_BASE_URL}/holidays/building/${buildingId}/calendar?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(token),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching holiday calendar:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to fetch holiday calendar',
      },
    };
  }
}

/**
 * Get holiday by ID
 * @param id - Holiday ID
 * @param token - Authentication token
 * @returns Promise with holiday data
 */
export async function getHoliday(
  id: string,
  token: string
): Promise<GetHolidayByIdResponse> {
  try {
    const url = `${API_BASE_URL}/holidays/${id}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(token),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching holiday:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to fetch holiday',
      },
    };
  }
}

/**
 * Create new holiday
 * @param data - Holiday data
 * @param token - Authentication token
 * @returns Promise with created holiday
 */
export async function createHoliday(
  data: CreateHolidayData,
  token: string
): Promise<CreateHolidayResponse> {
  try {
    const url = `${API_BASE_URL}/holidays`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: createHeaders(token),
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating holiday:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to create holiday',
      },
    };
  }
}

/**
 * Update holiday
 * @param id - Holiday ID
 * @param data - Update data
 * @param token - Authentication token
 * @returns Promise with updated holiday
 */
export async function updateHoliday(
  id: string,
  data: UpdateHolidayData,
  token: string
): Promise<UpdateHolidayResponse> {
  try {
    const url = `${API_BASE_URL}/holidays/${id}`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: createHeaders(token),
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating holiday:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to update holiday',
      },
    };
  }
}

/**
 * Delete holiday
 * @param id - Holiday ID
 * @param token - Authentication token
 * @returns Promise with deletion result
 */
export async function deleteHoliday(
  id: string,
  token: string
): Promise<DeleteHolidayResponse> {
  try {
    const url = `${API_BASE_URL}/holidays/${id}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: createHeaders(token),
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting holiday:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to delete holiday',
      },
    };
  }
}

/**
 * Get all buildings for holiday management
 * @param token - Authentication token
 * @returns Promise with buildings data
 */
export async function getBuildings(token: string): Promise<GetBuildingsResponse> {
  try {
    const url = `${API_BASE_URL}/holidays/buildings`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: createHeaders(token),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: {
          code: errorData.error?.code || 'HTTP_ERROR',
          message: errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`,
        },
      };
    }

    const responseData = await response.json();
    
    // Extract data from API response wrapper
    if (responseData.success && responseData.data !== undefined) {
      return {
        success: true,
        data: responseData.data, // Direct array of buildings
      };
    }
    
    return {
      success: false,
      error: {
        code: 'INVALID_RESPONSE',
        message: 'Invalid response format from server',
      },
    };
  } catch (error) {
    console.error('Error fetching buildings:', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to fetch buildings',
      },
    };
  }
}
